import React, { useEffect, useState, useMemo } from "react";
import { Plus, Trash2, Search, X, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { COUNTRIES } from "@/lib/countries";
import ImageField from "@/components/admin/ImageField";

/**
 * Admin editor for the "Our Global Love Stories" world map.
 * Add or remove countries + optionally attach a B&W couple portrait per country.
 * The portrait appears on hover of the corresponding ✓ on the public map.
 */
export default function NationalitiesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [photoEditFor, setPhotoEditFor] = useState(null); // {id, name, photo_url}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/couple-nationalities");
      setRows(r.data || []);
    } catch {
      toast.error("Could not load nationalities");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const selectedCodes = useMemo(() => new Set(rows.map((r) => r.code)), [rows]);

  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    return COUNTRIES
      .filter((c) => !selectedCodes.has(c.code))
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [query, selectedCodes]);

  const add = async (c) => {
    setBusy(true);
    try {
      await api.post("/admin/couple-nationalities", { code: c.code, name: c.name });
      toast.success(`Added ${c.name}`);
      setPickerOpen(false);
      setQuery("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.name} from the map?`)) return;
    try {
      await api.delete(`/admin/couple-nationalities/${row.id}`);
      toast.success(`Removed ${row.name}`);
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  const savePhoto = async (photo_url) => {
    if (!photoEditFor) return;
    try {
      await api.put(`/admin/couple-nationalities/${photoEditFor.id}`, { photo_url });
      toast.success(photo_url ? "Photo attached" : "Photo removed");
      // Keep modal open so admin can immediately verify; refresh underlying rows
      setPhotoEditFor((prev) => (prev ? { ...prev, photo_url } : prev));
      load();
    } catch {
      toast.error("Could not save photo");
    }
  };

  return (
    <div data-testid="nationalities-admin" className="space-y-6">
      <div className="border border-white/10 bg-black/40 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">Homepage · Our Global Love Stories</div>
            <h3 className="font-serif text-2xl mt-2 font-light">Wedding Couple Nationalities</h3>
            <p className="text-white/60 text-sm mt-2 max-w-xl leading-relaxed">
              Add the nationality / cultural background of each couple you've captured. Each entry
              places one gold ✓ on the world map on your homepage. Attach a portrait to have it
              appear as a small black-and-white photo when someone hovers the ✓.
            </p>
          </div>
          <button
            data-testid="nationality-add-btn"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black text-sm tracking-wide hover:bg-[#e6c250] transition">
            <Plus size={16} /> Add country
          </button>
        </div>
      </div>

      <div className="border border-white/10">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between text-xs uppercase tracking-widest text-white/50">
          <span data-testid="nationality-count">{rows.length} {rows.length === 1 ? "country" : "countries"} on the map</span>
        </div>
        {loading ? (
          <div className="p-6 text-white/50 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-white/50 text-sm">
            No countries yet. Click <b>Add country</b> to place your first gold ✓.
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {rows.map((row) => (
              <li key={row.id} data-testid={`nationality-row-${row.code}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition">
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center justify-center w-9 h-9 border border-[#D4AF37]/30 text-[#D4AF37] font-serif text-sm tracking-wider">
                    {row.code}
                  </span>
                  {row.photo_url ? (
                    <img
                      src={row.photo_url}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover grayscale border border-[#D4AF37]/30"
                      data-testid={`nationality-photo-${row.code}`}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full border border-dashed border-white/15 flex items-center justify-center text-white/25">
                      <ImageIcon size={14} />
                    </div>
                  )}
                  <div>
                    <div className="text-sm">{row.name}</div>
                    <div className="text-xs text-white/40">
                      {row.photo_url ? "Portrait attached · shown on map hover" : "Placed on the map with a gold ✓"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`nationality-edit-photo-${row.code}`}
                    onClick={() => setPhotoEditFor(row)}
                    className="text-white/50 hover:text-[#D4AF37] transition text-xs tracking-widest uppercase px-3 py-2"
                    aria-label={`Edit photo for ${row.name}`}>
                    {row.photo_url ? "Change photo" : "Add photo"}
                  </button>
                  <button
                    data-testid={`nationality-remove-${row.code}`}
                    onClick={() => remove(row)}
                    className="text-white/50 hover:text-red-400 transition p-2"
                    aria-label={`Remove ${row.name}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setPickerOpen(false)}
             data-testid="nationality-picker">
          <div className="w-full max-w-lg bg-[#0a0a0a] border border-white/15" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <h4 className="font-serif text-lg">Choose a country</h4>
              <button onClick={() => setPickerOpen(false)} className="text-white/50 hover:text-white p-1"
                      data-testid="nationality-picker-close" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="px-5 pt-4">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  autoFocus
                  data-testid="nationality-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search 170+ countries…"
                  className="w-full bg-black border border-white/15 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>
            <ul className="max-h-[55vh] overflow-auto mt-3 pb-3">
              {available.length === 0 && (
                <li className="px-5 py-6 text-center text-white/40 text-sm">No countries match.</li>
              )}
              {available.map((c) => (
                <li key={c.code}>
                  <button
                    data-testid={`nationality-option-${c.code}`}
                    disabled={busy}
                    onClick={() => add(c)}
                    className="w-full flex items-center gap-4 px-5 py-2.5 text-left hover:bg-white/5 transition disabled:opacity-50">
                    <span className="inline-flex items-center justify-center w-8 h-8 border border-white/15 text-white/70 text-xs">
                      {c.code}
                    </span>
                    <span className="text-sm">{c.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {photoEditFor && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setPhotoEditFor(null)}
             data-testid="nationality-photo-editor">
          <div className="w-full max-w-md bg-[#0a0a0a] border border-white/15" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div>
                <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">Couple Portrait</div>
                <h4 className="font-serif text-lg mt-1">{photoEditFor.name}</h4>
              </div>
              <button onClick={() => setPhotoEditFor(null)} className="text-white/50 hover:text-white p-1"
                      data-testid="nationality-photo-editor-close" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-white/50 leading-relaxed">
                Upload a portrait of the couple — landscape or square works best. It will appear as a
                small black-and-white portrait when someone hovers on the ✓ for this country. Leave empty
                to keep only the gold ✓.
              </p>
              <ImageField
                value={photoEditFor.photo_url || ""}
                onChange={savePhoto}
                aspect="aspect-square"
                variant="medium"
                testId="nationality-photo-upload"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
